/**
 * Export utilities for SurveyPro ERP
 * Supports CSV, Excel (pure-browser OOXML, no third-party deps), and PDF export formats
 */

// ── CSV Export ────────────────────────────────────────────────────────────────
export function exportToCSV(data: Record<string, unknown>[], filename: string) {
  if (!data.length) return;

  const headers = Object.keys(data[0]);
  const rows = data.map((row) =>
    headers
      .map((h) => {
        const val = row[h];
        if (val === null || val === undefined) return '';
        const str = String(val).replace(/"/g, '""');
        return str.includes(',') || str.includes('\n') || str.includes('"') ? `"${str}"` : str;
      })
      .join(',')
  );

  const csv = [headers.join(','), ...rows].join('\n');
  downloadBlob(csv, `${filename}.csv`, 'text/csv;charset=utf-8;');
}

// ── Excel Export (pure-browser OOXML — no xlsx / SheetJS dependency) ─────────
export async function exportToExcel(
  data: Record<string, unknown>[],
  filename: string,
  sheetName = 'Sheet1'
) {
  if (!data.length) return;

  const headers = Object.keys(data[0]);

  // Build cell XML helpers
  const escapeXml = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    return String(v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const colLetter = (idx: number): string => {
    let s = '';
    let n = idx + 1;
    while (n > 0) {
      const rem = (n - 1) % 26;
      s = String.fromCharCode(65 + rem) + s;
      n = Math.floor((n - 1) / 26);
    }
    return s;
  };

  const buildCell = (rowIdx: number, colIdx: number, value: unknown): string => {
    const ref = `${colLetter(colIdx)}${rowIdx}`;
    const num = typeof value === 'number' ? value : (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '' ? Number(value) : null);
    if (num !== null) {
      return `<c r="${ref}"><v>${num}</v></c>`;
    }
    const str = escapeXml(value);
    return `<c r="${ref}" t="inlineStr"><is><t>${str}</t></is></c>`;
  };

  // Header row (row 1)
  const headerCells = headers.map((h, ci) => buildCell(1, ci, h)).join('');
  const headerRow = `<row r="1">${headerCells}</row>`;

  // Data rows
  const dataRows = data.map((row, ri) => {
    const cells = headers.map((h, ci) => buildCell(ri + 2, ci, row[h])).join('');
    return `<row r="${ri + 2}">${cells}</row>`;
  }).join('');

  const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>${headerRow}${dataRows}</sheetData>
</worksheet>`;

  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
          xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;

  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet"
    Target="worksheets/sheet1.xml"/>
</Relationships>`;

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml"
    ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml"
    ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument"
    Target="xl/workbook.xml"/>
</Relationships>`;

  // Build ZIP using browser-native CompressionStream (supported in all modern browsers)
  // Fallback: use a minimal ZIP builder if CompressionStream is unavailable
  const zipBytes = await buildZip([
    { name: '[Content_Types].xml',          data: contentTypes },
    { name: '_rels/.rels',                  data: rootRels },
    { name: 'xl/workbook.xml',              data: workbookXml },
    { name: 'xl/_rels/workbook.xml.rels',   data: workbookRels },
    { name: 'xl/worksheets/sheet1.xml',     data: sheetXml },
  ]);

  const blob = new Blob([zipBytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, `${filename}.xlsx`, '');
}

// ── Minimal ZIP builder (stored, no compression — universally compatible) ─────
interface ZipEntry { name: string; data: string; }

async function buildZip(entries: ZipEntry[]): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const localHeaders: Uint8Array[] = [];
  const centralDirs: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = enc.encode(entry.name);
    const dataBytes = enc.encode(entry.data);
    const crc = crc32(dataBytes);
    const size = dataBytes.length;

    // Local file header
    const lh = new Uint8Array(30 + nameBytes.length);
    const lhView = new DataView(lh.buffer);
    lhView.setUint32(0, 0x04034b50, true);  // signature
    lhView.setUint16(4, 20, true);           // version needed
    lhView.setUint16(6, 0, true);            // flags
    lhView.setUint16(8, 0, true);            // compression: stored
    lhView.setUint16(10, 0, true);           // mod time
    lhView.setUint16(12, 0, true);           // mod date
    lhView.setUint32(14, crc, true);         // crc32
    lhView.setUint32(18, size, true);        // compressed size
    lhView.setUint32(22, size, true);        // uncompressed size
    lhView.setUint16(26, nameBytes.length, true);
    lhView.setUint16(28, 0, true);           // extra field length
    lh.set(nameBytes, 30);

    // Central directory entry
    const cd = new Uint8Array(46 + nameBytes.length);
    const cdView = new DataView(cd.buffer);
    cdView.setUint32(0, 0x02014b50, true);   // signature
    cdView.setUint16(4, 20, true);           // version made by
    cdView.setUint16(6, 20, true);           // version needed
    cdView.setUint16(8, 0, true);            // flags
    cdView.setUint16(10, 0, true);           // compression
    cdView.setUint16(12, 0, true);           // mod time
    cdView.setUint16(14, 0, true);           // mod date
    cdView.setUint32(16, crc, true);         // crc32
    cdView.setUint32(20, size, true);        // compressed size
    cdView.setUint32(24, size, true);        // uncompressed size
    cdView.setUint16(28, nameBytes.length, true);
    cdView.setUint16(30, 0, true);           // extra
    cdView.setUint16(32, 0, true);           // comment
    cdView.setUint16(34, 0, true);           // disk start
    cdView.setUint16(36, 0, true);           // internal attr
    cdView.setUint32(38, 0, true);           // external attr
    cdView.setUint32(42, offset, true);      // local header offset
    cd.set(nameBytes, 46);

    localHeaders.push(lh, dataBytes);
    centralDirs.push(cd);
    offset += lh.length + dataBytes.length;
  }

  const cdSize = centralDirs.reduce((s, b) => s + b.length, 0);
  const eocd = new Uint8Array(22);
  const eocdView = new DataView(eocd.buffer);
  eocdView.setUint32(0, 0x06054b50, true);  // signature
  eocdView.setUint16(4, 0, true);           // disk number
  eocdView.setUint16(6, 0, true);           // start disk
  eocdView.setUint16(8, entries.length, true);
  eocdView.setUint16(10, entries.length, true);
  eocdView.setUint32(12, cdSize, true);
  eocdView.setUint32(16, offset, true);
  eocdView.setUint16(20, 0, true);          // comment length

  const all = [...localHeaders, ...centralDirs, eocd];
  const total = all.reduce((s, b) => s + b.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const b of all) { out.set(b, pos); pos += b.length; }
  return out;
}

// CRC-32 implementation (standard polynomial)
function crc32(data: Uint8Array): number {
  const table = makeCrcTable();
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = (crc >>> 8) ^ table[(crc ^ data[i]) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
}

let _crcTable: Uint32Array | null = null;
function makeCrcTable(): Uint32Array {
  if (_crcTable) return _crcTable;
  _crcTable = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    _crcTable[i] = c;
  }
  return _crcTable;
}

// ── PDF Export (Print to PDF) ─────────────────────────────────────────────────
export function exportToPDF(
  elementId: string,
  filename: string,
  title?: string
) {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element #${elementId} not found for PDF export`);
    return;
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups to print PDF');
    return;
  }

  const styles = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join('\n');
      } catch {
        return '';
      }
    })
    .join('\n');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title || filename}</title>
      <style>
        ${styles}
        @media print {
          body { margin: 0; padding: 20px; }
          .no-print { display: none !important; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f5f5f5; }
        }
      </style>
    </head>
    <body>
      ${title ? `<h2 style="margin-bottom: 16px; color: #111; font-family: system-ui, sans-serif;">${title}</h2>` : ''}
      ${element.outerHTML}
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
            window.close();
          }, 200);
        };
      <\/script>
    </body>
    </html>
  `);
  printWindow.document.close();
}

// ── Helper ────────────────────────────────────────────────────────────────────
function downloadBlob(data: string | Blob | Uint8Array, filename: string, mimeType: string) {
  const blob =
    data instanceof Blob
      ? data
      : data instanceof Uint8Array
      ? new Blob([data], { type: mimeType })
      : new Blob([data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ── Formatters for export data ────────────────────────────────────────────────
export function formatCurrency(value: number | string | undefined): string {
  if (value === undefined || value === null) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  return `\u20B9${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(dateStr);
  }
}

export function sanitizeForExcel(value: unknown): unknown {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value;
  const str = String(value);
  // Prevent Excel formula injection
  if (/^[+=@-]/.test(str)) return `'${str}`;
  return str;
}
